// Searchable multi-select (ARIA 1.2 combobox with a listbox popup). The person types to
// filter, picks from the matches with the mouse, touch or keyboard, and sees each choice as
// a removable chip. Keyboard: Down/Up move through the matches, Enter picks, Escape
// closes, Backspace in an empty box removes the last chip. Every target is at least 44px.
import { useId, useMemo, useRef, useState } from 'react'
import { cx } from '@/components/ui'

export type ComboOption = {
  value: string
  label: string
  /** Shown above the options it heads (not selectable). */
  group?: string
  /** Extra words the filter matches (not shown). */
  keywords?: string
}

/** Options whose label, group or keywords contain every typed word (case-insensitive). */
export function filterOptions(options: readonly ComboOption[], query: string): ComboOption[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return [...options]
  return options.filter((o) => {
    const hay = `${o.label} ${o.group ?? ''} ${o.keywords ?? ''}`.toLowerCase()
    return words.every((w) => hay.includes(w))
  })
}

export function MultiCombobox({ id, options, value, onChange, placeholder, disabled, emptyText = 'No matching activity' }: {
  id?: string
  options: readonly ComboOption[]
  value: readonly string[]
  onChange: (next: string[]) => void
  placeholder?: string
  disabled?: boolean
  emptyText?: string
}) {
  const auto = useId()
  const inputId = id ?? `${auto}-input`
  const listId = `${auto}-list`
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const matches = useMemo(() => filterOptions(options, query).filter((o) => !value.includes(o.value)), [options, query, value])
  const activeOption = open ? matches[Math.min(active, matches.length - 1)] : undefined
  const chosen = value.map((v) => options.find((o) => o.value === v) ?? { value: v, label: `${v} (not in the list)` })

  const pick = (o: ComboOption) => {
    onChange([...value, o.value])
    setQuery('')
    setActive(0)
    inputRef.current?.focus()
  }
  const remove = (v: string) => {
    onChange(value.filter((x) => x !== v))
    inputRef.current?.focus()
  }

  return (
    <div>
      {chosen.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-2" aria-label="Chosen">
          {chosen.map((o) => (
            <li key={o.value} className="flex min-h-[44px] max-w-full items-center gap-1 rounded-lg border border-brand bg-brand-light pl-3 text-sm text-ink">
              <span className="min-w-0 py-2">{o.label}</span>
              <button
                type="button"
                className="tap flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg font-semibold text-brand-dark hover:bg-white/60"
                aria-label={`Remove ${o.label}`}
                disabled={disabled}
                onClick={() => remove(o.value)}
              >
                <span aria-hidden>x</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeOption ? `${listId}-${activeOption.value}` : undefined}
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          className="w-full min-h-[46px] rounded-lg border border-line-strong bg-white px-3 py-2 leading-normal text-ink placeholder:text-ink-faint"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setOpen(true)
              setActive((a) => Math.min(a + 1, Math.max(matches.length - 1, 0)))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActive((a) => Math.max(a - 1, 0))
            } else if (e.key === 'Enter') {
              if (open && activeOption) {
                e.preventDefault()
                pick(activeOption)
              }
            } else if (e.key === 'Escape') {
              setOpen(false)
            } else if (e.key === 'Backspace' && query === '' && value.length > 0) {
              onChange(value.slice(0, -1))
            }
          }}
        />
        {open && !disabled ? (
          <ul
            id={listId}
            role="listbox"
            aria-multiselectable="true"
            aria-label="Matching activities"
            className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-line-strong bg-white shadow-lg"
          >
            {matches.length === 0 ? <li className="p-3 text-sm text-ink-faint">{emptyText}</li> : null}
            {matches.map((o, i) => {
              const header = o.group && (i === 0 || matches[i - 1].group !== o.group)
              return (
                <li key={o.value} role="presentation">
                  {header ? <p className="bg-paper px-3 py-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">{o.group}</p> : null}
                  <div
                    id={`${listId}-${o.value}`}
                    role="option"
                    aria-selected={activeOption?.value === o.value}
                    className={cx('flex min-h-[44px] cursor-pointer items-center px-3 py-2 text-sm text-ink', activeOption?.value === o.value && 'bg-brand-light')}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(o)}
                  >
                    {o.label}
                  </div>
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>
    </div>
  )
}
